/*
 This file is part of GNU Taler
 (C) 2021-2024 Taler Systems S.A.

 GNU Taler is free software; you can redistribute it and/or modify it under the
 terms of the GNU General Public License as published by the Free Software
 Foundation; either version 3, or (at your option) any later version.

 GNU Taler is distributed in the hope that it will be useful, but WITHOUT ANY
 WARRANTY; without even the implied warranty of MERCHANTABILITY or FITNESS FOR
 A PARTICULAR PURPOSE.  See the GNU General Public License for more details.

 You should have received a copy of the GNU General Public License along with
 GNU Taler; see the file COPYING.  If not, see <http://www.gnu.org/licenses/>
 */

/**
 *
 * @author Sebastian Javier Marchano (sebasjm)
 */
import {
  Amounts,
  AmountString
} from "@gnu-taler/taler-util";
import { ComponentChildren, h, VNode } from "preact";
import { useCurrenciesContext } from "../../context/currency.js";
import { useSessionContext } from "../../context/session.js";
import { InputWithAddon } from "./InputWithAddon.js";
import { InputProps, useField } from "./useField.js";

export interface Props<T> extends InputProps<T> {
  expand?: boolean;
  addonAfter?: ComponentChildren;
  children?: ComponentChildren;
  side?: ComponentChildren;
  focus?: boolean;
}

export function InputCurrency<T>({
  name,
  readonly,
  label,
  placeholder,
  help,
  tooltip,
  expand,
  addonAfter,
  children,
  focus,
  side,
}: Props<keyof T>): VNode {
  const { config } = useSessionContext();
  const { currency } = useCurrenciesContext();
  const { value } = useField<T>(name);
  const parsedValue = !value ? undefined : Amounts.parse(value);

  console.log("intpu currency", currency);
  // if the field already has a value, use the
  // currency of that value
  // fallback to the selected currency on the session
  const cSpec = parsedValue?.currency
    ? config.currencies[parsedValue.currency]
    : currency;

  const cId = parsedValue?.currency ?? currency?.id;

  const id = cSpec?.num_fractional_input_digits;
  const step = !id ? 0.1 : Math.pow(10, -1 * (id ?? 0));

  return (
    <InputWithAddon<T>
      name={name}
      readonly={readonly || cId === undefined}
      addonBefore={cId ?? ""}
      side={side}
      label={label}
      placeholder={placeholder}
      help={help}
      focus={focus}
      tooltip={tooltip}
      addonAfter={addonAfter}
      inputType="decimal"
      expand={expand}
      toStr={(v?: AmountString) => v?.split(":")[1] || ""}
      fromStr={(v: string) => (!v ? undefined : `${cId ?? ""}:${v}`)}
      inputExtra={{ min: 0, step }}
    >
      {children}
    </InputWithAddon>
  );
}
